import { ReactNode, PureComponent, ReactElement, lazy } from 'react';
import _ from 'lodash';
import { IWidgetThingsClass } from "../../Utils/IWidgetThingsClass";
import * as WidgetsThingsStructs from "../../WidgetsThingsStructs";
import { IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDescription, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { WebThingPort, WebThing } from '@API/WebThing';
import { WebThingPortType } from '@API/WebNodeTypes';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';

import { InfoCardsData, InfoCardsKeys } from './InfoCardsCoreStruct';
const InfoCards = lazy(() => import("./InfoCardsCore"));

// =================================================================================================
class InfoCardsSettings extends PureComponent<IWidgetSettingsProps<InfoCardsData>> {
    constructor(props: IWidgetSettingsProps<InfoCardsData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<InfoCardsData>[] = [
            {
                name: "Columns",
                elements: [
                    {
                        type: SettingsType.ColorPicker,
                        label: "Background Color",
                        dataPath: "columnsTheme.backgroundColor"
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: "Font Color",
                        dataPath: "columnsTheme.fontColor"
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Shadow",
                        dataPath: "columnsTheme.shadowLevel",
                        minValue: 0,
                        maxValue: 5,
                        stepValue: 1,
                        valuePosfix: ""
                    }
                ]
            },
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: "Fill Height",
                        dataPath: "fillHeight"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Autofit column height",
                        dataPath: "autofitColumnHeight"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Fill Width",
                        dataPath: "fillWidth"
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Min Column Width",
                        dataPath: "minColumnWidth",
                        minValue: 250,
                        maxValue: 1000,
                        stepValue: 10,
                        valuePosfix: "px"
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
        />;
    }
}
// =================================================================================================
export class InfoCardsWidget extends IWidgetThingsClass<InfoCardsData> {
    Render(key: string, width: number, height: number): ReactNode {
        return <WidgetsThingsStructs.LazyWidget >
            <InfoCards />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <InfoCardsSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(): WebThingPort[] {
        let ports: WebThingPort[] = [];

        // TODO: Set the Ports
        ports.push(new WebThingPort(
            {
                Name: 'Data',
                PortKey: InfoCardsKeys.Data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(InfoCardsKeys.Data, data))
        );

        ports.push(new WebThingPort(
            {
                Name: 'MoveEvent',
                PortKey: InfoCardsKeys.MoveEvent,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            },
            WebThingPortType.Data)
        );

        ports.push(new WebThingPort(
            {
                Name: 'ClickEvent',
                PortKey: InfoCardsKeys.ClickEvent,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            },
            WebThingPortType.Data)
        );

        ports.push(new WebThingPort({
            Name: 'OnDataChange',
            PortKey: InfoCardsKeys.OnDataChange,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            State: ''
        }, WebThingPortType.Trigger));

        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        // Add thing's ports.
        webThing.Ports = {};
        this.createThingPorts().forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts();
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): InfoCardsData {
        return {
            columnsTheme: {
                backgroundColor: '#337ab7',
                dragBackgroundColor: '#4caf50',
                fontColor: "white",
                shadowLevel: 2,
                boardRadius: 10,
            },
            fillHeight: false,
            autofitColumnHeight: false,
            fillWidth: true,
            minColumnWidth: 350
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return InfoCardsWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            Title: 'Info Cards',
            WidgetTitle: '',
            Key: 'InfoCardsWidget',
            Type: 'InfoCardsWidget',
            Data: InfoCardsWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // todo: set schema validators
        return { valid: true, errors: "" };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: InfoCardsData): InfoCardsData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}